import * as React from 'react';

import { Progress } from '@lightspeed/flame/Progress';

/**
 * Will only allow progress to expand beyond the current batch up to the next one.
 * Will prevent the progress bar from jumping back after being updated with the real progress.
 */
const calculateMaxProgress = (
  _progress: number,
  totalBatches: number,
  minProgress: number
): number => {
  // avoid multiplication and division by 0
  const progress = _progress === 0 ? 0.001 : _progress;
  if (totalBatches === 0) return minProgress;
  const currentBatchIndex = Math.floor((progress / 100) * totalBatches);
  const maxProgress = Math.ceil(((currentBatchIndex + 1) / totalBatches) * 100);
  if (maxProgress >= 100) return 100;
  return minProgress > maxProgress ? minProgress : maxProgress;
};

const minMaxProgress = (progress: number, minProgress: number, maxProgress: number): number => {
  if (progress < minProgress) return minProgress;
  if (progress > 100) return 100;
  if (progress > maxProgress) return maxProgress;
  return progress;
};

interface Props {
  progress: number;
  totalBatches: number;
  minProgress?: number;
  updateInterval?: number;
  updateIncrement?: number;
}

const ProgressAsyncJob: React.FC<Props> = ({
  progress = 0,
  totalBatches = 0,
  minProgress = 2,
  updateInterval = 500,
  updateIncrement = 1,
}) => {
  const [jobProgress, setJobProgress] = React.useState(minProgress);

  React.useEffect(() => {
    const maxProgress = calculateMaxProgress(progress, totalBatches, minProgress);
    const interval = setInterval(() => {
      setJobProgress((prevProgress) => {
        const nextProgress = progress > prevProgress ? progress : prevProgress + updateIncrement;
        return minMaxProgress(nextProgress, minProgress, maxProgress);
      });
    }, updateInterval);
    return () => clearInterval(interval);
  }, [progress, totalBatches, minProgress, updateInterval, updateIncrement]);

  return <Progress type="determinate" total={100} current={jobProgress} />;
};

export default ProgressAsyncJob;
